import { FormBuilder, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormValidation } from '../../classes/formValidation';
import { eSoilWebCrawErrors } from '../../interfaces/constants';
import { ISoilLayer, ISoilWebProperties } from '../../models/soil-type/interfaces';
import { SoilTypeService } from '../../models/soil-type/service';

export type CustomSoilPropertiesFields = 'Layers';

export class CustomSoilPropertiesFormBuilder {
	private _fb: FormBuilder;
	private _customSoilLayers: ISoilLayer[]; // custom soil layer data with null values
	public diffs: ISoilLayer[]; // difference between custom soil layer (initial) and soil web
	private _subscriptions$: Subject<boolean>;

	public areSoilLayersCustomized: boolean;
	public form: FormGroup;
	public soilWebProperties: ISoilWebProperties; // this is accessed in a template

	public get f(): { [key in CustomSoilPropertiesFields]: AbstractControl } {
		return this.form.controls as
			{ [key in CustomSoilPropertiesFields]: AbstractControl };
	}

	constructor(fb: FormBuilder,
		customSoilLayers: ISoilLayer[],
		$subscription: Subject<boolean>) {

		this._fb = fb;
		this._subscriptions$ = $subscription;
		this._customSoilLayers = customSoilLayers;
	}

	private _initialize(soilWebLayers: ISoilLayer[]): FormGroup {
		this.diffs = this._generateDiffs(this._customSoilLayers, soilWebLayers);

		this._customSoilLayers = this._normalizeCustomProperies(this._customSoilLayers,
			soilWebLayers);

		this.form = this._fb.group({
			Layers: this._fb.array([]),
			AverageMineralization: null
		});

		this._addLayersToForm(this._customSoilLayers);
		this._bindValueChanges();
		this.areSoilLayersCustomized = this._areSoilPropertiesCustomized();

		return this.form;
	}

	public hasChangesbeenMade(): boolean {
		return this.form.dirty;
	}

	/**
	 * Compares custom soil properties with soil web and generate an array containing
	 * diffs of values
	 * @param customSoilLayers
	 * @param soilWebLayers
	 * @returns
	 */
	private _generateDiffs(customSoilLayers: ISoilLayer[], soilWebLayers: ISoilLayer[]): ISoilLayer[] {
		let result: ISoilLayer[];

		result = [];

		if (!customSoilLayers || customSoilLayers.length === 0 || !soilWebLayers || soilWebLayers.length === 0) {
			return [];
		}

		for (let customSoilLayer of customSoilLayers) {
			let soilWebLayer: ISoilLayer;
			let item: ISoilLayer;

			soilWebLayer = soilWebLayers.find(x => x.Depth === customSoilLayer.Depth);

			result.push({
				Depth: customSoilLayer.Depth,
				Density: customSoilLayer.Density ? (soilWebLayer.Density - customSoilLayer.Density) : 0,
				Silt: customSoilLayer.Silt ? (soilWebLayer.Silt - customSoilLayer.Silt) : 0,
				Sand: customSoilLayer.Sand ? (soilWebLayer.Sand - customSoilLayer.Sand) : 0,
				Organic: customSoilLayer.Organic ? (soilWebLayer.Organic - customSoilLayer.Organic) : 0,
				Tension: customSoilLayer.Tension ? (soilWebLayer.Tension - customSoilLayer.Tension) : 0,
				Mineralization: customSoilLayer.Mineralization ? (soilWebLayer.Mineralization - customSoilLayer.Mineralization) : 0
			});
		}

		return result;
	}

	/**
	 * Method triggered when user either clicks on a map, or types in coordinates, or
	 * if user clicks the "Reset" button
	 * @param coordinates
	 * @param source soil type source. 1 = certified, 0 = UC Davis. This should actually not be boolean
	 * this field is typically not used unless user clicks the Reset link
	 */
	public initializeWithCoordinates(coordinates: string, soilService: SoilTypeService): Promise<eSoilWebCrawErrors> {

		return soilService.getSoilWebRecord(coordinates).then(data => {
			let properties: ISoilWebProperties;

			properties = data as ISoilWebProperties;

			if (properties.Error) {
				this.soilWebProperties = properties;
				return properties.Error as eSoilWebCrawErrors;
			}

			if (!this.form) {
				this.soilWebProperties = properties;
				this._initialize(this.soilWebProperties.Layers);
			} else {
				if (this._hasSoilTypeChanged(properties)) {
					this.soilWebProperties = properties;
					this.resetSoilProperties();
				} else {
					this.soilWebProperties = properties;
				}
			}

			return null;
		});
	}

	public initializeWithStaticData(soilWebProperties: ISoilWebProperties): void {

		if (!soilWebProperties) {
			throw new Error('soil web properties is null');
		}

		if (!this.form) {
			this.soilWebProperties = soilWebProperties;
			this._initialize(soilWebProperties.Layers);
		} else {
			if (this._hasSoilTypeChanged(soilWebProperties)) {
				this.soilWebProperties = soilWebProperties;
				this.resetSoilProperties();
				return;
			}
		}
	}

	/**
	 * Prunes custom soil layer values that match Soil Web values to
	 * identify user edited values that are different than Soil Web values
	 * for saving
	 * @returns soil layers
	 * @throws error if soil web layer isn't found
	 */
	public cullRedundantFields(): ISoilLayer[] {
		let result: ISoilLayer[];

		result = this.f.Layers.value;

		for (let layer of result) {
			let soilWebLayer: ISoilLayer;

			soilWebLayer = this.soilWebProperties.Layers.find(x => x.Depth === layer.Depth);

			if (!soilWebLayer) {
				throw new Error('soil web layer not found');
			}

			layer.Density = layer.Density === soilWebLayer.Density ? null : layer.Density;
			layer.Mineralization = layer.Mineralization === soilWebLayer.Mineralization ? null : layer.Mineralization;
			layer.Organic = layer.Organic === soilWebLayer.Organic ? null : layer.Organic;
			layer.Sand = layer.Sand === soilWebLayer.Sand ? null : layer.Sand;
			layer.Silt = layer.Silt === soilWebLayer.Silt ? null : layer.Silt;
			layer.Tension = layer.Tension === soilWebLayer.Tension ? null : layer.Tension;
		}

		return result;
	}

	/**
	 * Resets
	 * @throws Error if soil web layer is not found
	 */
	public resetSoilProperties(): void {

		if (!this.soilWebProperties || !this.soilWebProperties.Layers ||
			this.soilWebProperties.Layers.length === 0) {

			throw new Error('soilWeb properties not found');
		}

		for (let layerControl of (this.f.Layers as FormArray).controls) {
			let soilWebLayer: ISoilLayer;

			soilWebLayer = this.soilWebProperties.Layers.find(x => x.Depth === layerControl.get('Depth').value);

			if (!soilWebLayer) {
				throw new Error('soil web layer not found');
			}

			layerControl.get('Density').setValue(soilWebLayer.Density, { emitEvent: false });
			layerControl.get('Sand').setValue(soilWebLayer.Sand, { emitEvent: false });
			layerControl.get('Mineralization').setValue(soilWebLayer.Mineralization, { emitEvent: false });
			layerControl.get('Organic').setValue(soilWebLayer.Organic, { emitEvent: false });
			layerControl.get('Silt').setValue(soilWebLayer.Silt, { emitEvent: false });
			layerControl.get('Tension').setValue(soilWebLayer.Tension, { emitEvent: false });
		}

		this.areSoilLayersCustomized = false;
		this.diffs = this._generateDiffs(this.soilWebProperties.Layers, this.soilWebProperties.Layers);
	}

	/**
	 * Determines whether soilWeb soil type changed based on coKey.
	 * Different coordinates may share the same soil properties.
	 * @param data
	 * @returns
	 */
	private _hasSoilTypeChanged(data: ISoilWebProperties): boolean {
		if (!this.soilWebProperties || !this.soilWebProperties.Record) {
			return false;
		} else if (!data || !data.Record) {
			return false;
		} else if (this.soilWebProperties.Record.CoKey === data.Record.CoKey) {
			return false;
		} else {
			return true;
		}
	}

	private _getBlankCustomLayers(): ISoilLayer[] {
		let result: ISoilLayer[];

		result = [
			{
				Depth: 1,
			},
			{
				Depth: 2,
			},
			{
				Depth: 3,
			},
			{
				Depth: 4
			}
		];

		return result;
	}

	/**
	 * A method used to fill custom soil layers with soil web layer data to remove null values
	 * @param customLayers user customized soil properties
	 * @param soilWebLayers soil properties from SoilWeb
	 * @throws error if soil web layer doesn't exist
	 */
	private _normalizeCustomProperies(customLayers: ISoilLayer[], soilWebLayers: ISoilLayer[]): ISoilLayer[] {
		if (!customLayers || customLayers.length === 0) {
			customLayers = this._getBlankCustomLayers();
		}

		if (!soilWebLayers || soilWebLayers.length === 0) {
			throw new Error('Soil data not available. Please try again later.');
		}

		for (let layer of customLayers) {
			let soilWebLayer: ISoilLayer;

			soilWebLayer = soilWebLayers.find(x => x.Depth === layer.Depth);

			if (!soilWebLayer) {
				throw new Error('Soil web layer not found');
			}

			layer.Density = layer.Density ?  layer.Density : soilWebLayer.Density;
			layer.Mineralization = layer.Mineralization ? layer.Mineralization : soilWebLayer.Mineralization;
			layer.Organic = layer.Organic ? layer.Organic : soilWebLayer.Organic;
			layer.Sand = layer.Sand ? layer.Sand : soilWebLayer.Sand;
			layer.Silt = layer.Silt ? layer.Silt : soilWebLayer.Silt;
			layer.Tension = layer.Tension ? layer.Tension : soilWebLayer.Tension;
		}

		return customLayers;
	}

	/**
	 * Add an array of custom soil layers to reactive form
	 * @param layers
	 * @throws Error if no layers are detected. Also throws an error if soil web layer match is not found
	 */
	private _addLayersToForm(layers: ISoilLayer[]): void {
		if (!layers || layers.length === 0) {
			throw new Error('no layers detected');
		}

		for (let layer of layers) {
			(this.f.Layers as FormArray).push(this._fb.group({
				Depth: [layer.Depth, [Validators.required, Validators.min(1), Validators.max(4)]],
				Sand: [layer.Sand, [Validators.required, FormValidation.greaterThanValidator(0)]],
				Silt: [layer.Silt, [Validators.required, FormValidation.greaterThanValidator(0)]],
				Organic: [layer.Organic, [Validators.required, Validators.min(0)]],
				Density: [layer.Density, [Validators.required, FormValidation.greaterThanValidator(0)]],
				Tension: [layer.Tension, [Validators.required, FormValidation.greaterThanValidator(0)]],
				Mineralization: [layer.Mineralization, [Validators.required, Validators.min(0)]]
			}));
		}
	}

	/**
	 * Binds valueChanges to reactive form controls
	 */
	private _bindValueChanges(): void {
		this.f.Layers.valueChanges.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			this.areSoilLayersCustomized = this._areSoilPropertiesCustomized();
			this.diffs = this._generateDiffs(this.f.Layers.value, this.soilWebProperties.Layers);
		});
	}

	/**
	 * Determines whether the user customized soil properties, based on differences
	 * between the reactive form and properties from SoilWeb.
	 * @throws Error if soil web layer is not found
	 */
	private _areSoilPropertiesCustomized(): boolean {

		for (let i = 1; i <= 4; i++) {
			let soilWebLayer: ISoilLayer;
			let customLayer: ISoilLayer;

			soilWebLayer = this.soilWebProperties.Layers.find(x => x.Depth === i);
			customLayer = (this.f.Layers.value as ISoilLayer[]).find(x => x.Depth === i);

			if (!soilWebLayer) {
				throw new Error('Soil web layer for depth not found');
			} else if (!customLayer) {
				continue;
			}

			if (!this._areSoilLayersEqual(soilWebLayer, customLayer)) {
				return true;
			}
		}

		return false;
	}

	private _areSoilLayersEqual(a: ISoilLayer, b: ISoilLayer): boolean {
		if (a.Depth !== b.Depth) {
			return false;
		} else if (a.Density !== b.Density) {
			return false;
		} else if (a.Organic !== b.Organic) {
			return false;
		} else if (a.Sand !== b.Sand) {
			return false;
		} else if (a.Silt !== b.Silt) {
			return false;
		} else if (a.Tension !== b.Tension) {
			return false;
		} else {
			return true;
		}
	}
}
